import { classesInPortuguese, formatPeriodLabel } from './defectStatistics'

const colorPalette = [
  '#FF6B35',
  '#004E89',
  '#2EC4B6',
  '#E71D36',
  '#7B2CBF',
  '#2D6A4F',
  '#F72585',
  '#4361EE',
  '#FF9F1C',
  '#06D6A0',
  '#8338EC',
  '#3A86FF',
  '#FB5607'
]

function getBaseChartConfig () {
  return {
    chart: {
      id: 'statistics-chart',
      events: {
        dataPointSelection: () => false,
        click: () => false
      },
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: true
        }
      }
    },
    legend: {
      show: true,
      onItemClick: {
        toggleDataSeries: false
      },
      onItemHover: {
        highlightDataSeries: true
      }
    },
    colors: colorPalette,
    dataLabels: {
      enabled: false
    }
  }
}

function buildBoxPlotValues (stat) {
  const min = Number(stat && stat.min != null ? stat.min : 0)
  const mean = Number(stat && stat.mean != null ? stat.mean : 0)
  const max = Number(stat && stat.max != null ? stat.max : 0)
  const q1 = Number(((min + mean) / 2).toFixed(2))
  const q3 = Number(((mean + max) / 2).toFixed(2))
  return [min, q1, mean, q3, max]
}

function buildBarChart (selectedKeys, columnList, data, groupBy) {
  const categories = columnList.map(column => formatPeriodLabel(column, groupBy))

  if (columnList.length === 1) {
    const periodKey = columnList[0]
    const classEntries = selectedKeys
      .map(name => ({
        name: classesInPortuguese[name],
        value: data[periodKey][name] && data[periodKey][name].mean != null
          ? data[periodKey][name].mean
          : 0
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))

    const series = [{
      name: formatPeriodLabel(periodKey, groupBy),
      data: classEntries.map(item => item.value)
    }]

    const options = {
      ...getBaseChartConfig(),
      chart: {
        ...getBaseChartConfig().chart,
        type: 'bar'
      },
      plotOptions: {
        bar: {
          columnWidth: '55%',
          distributed: true
        }
      },
      xaxis: {
        categories: classEntries.map(item => item.name),
        tickPlacement: 'on',
        labels: {
          rotate: -35,
          hideOverlappingLabels: false,
          trim: false
        }
      },
      yaxis: {
        title: {
          text: 'Média (%)'
        }
      },
      legend: {
        show: false
      },
      tooltip: {
        y: {
          formatter: (val) => `${val} %`
        }
      }
    }

    return { options, series }
  }

  const series = selectedKeys
    .map(name => ({
      name: classesInPortuguese[name],
      data: columnList.map(column => {
        const value = data[column][name]
        return value && value.mean != null ? value.mean : 0
      })
    }))
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))

  const options = {
    ...getBaseChartConfig(),
    chart: {
      ...getBaseChartConfig().chart,
      type: 'bar'
    },
    plotOptions: {
      bar: {
        columnWidth: '80%'
      }
    },
    xaxis: {
      categories,
      tickPlacement: 'on'
    },
    yaxis: {
      title: {
        text: 'Média (%)'
      }
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val) => `${val} %`
      }
    }
  }

  return { options, series }
}

function buildBoxPlotChart (selectedKeys, columnList, data) {
  const seriesData = selectedKeys
    .map(name => {
      const stats = columnList
        .map(column => data[column][name])
        .filter(Boolean)

      const mins = stats.map(s => Number(s.min != null ? s.min : 0))
      const means = stats.map(s => Number(s.mean != null ? s.mean : 0))
      const maxs = stats.map(s => Number(s.max != null ? s.max : 0))

      const min = mins.length ? Math.min(...mins) : 0
      const mean = means.length
        ? Number((means.reduce((a, b) => a + b, 0) / means.length).toFixed(2))
        : 0
      const max = maxs.length ? Math.max(...maxs) : 0

      return {
        x: classesInPortuguese[name],
        y: buildBoxPlotValues({ min, mean, max })
      }
    })
    .sort((a, b) => a.x.localeCompare(b.x, 'pt-BR'))

  const series = [{
    type: 'boxPlot',
    data: seriesData
  }]

  const options = {
    ...getBaseChartConfig(),
    chart: {
      ...getBaseChartConfig().chart,
      type: 'boxPlot',
      parentHeightOffset: 0
    },
    plotOptions: {
      boxPlot: {
        colors: {
          upper: '#FF8C42',
          lower: '#004E89'
        }
      }
    },
    xaxis: {
      type: 'category',
      categories: seriesData.map(item => item.x),
      tickPlacement: 'on',
      labels: {
        show: true,
        rotate: -45,
        rotateAlways: true,
        hideOverlappingLabels: false,
        trim: false,
        showDuplicates: true,
        style: {
          fontSize: '11px'
        }
      }
    },
    yaxis: {
      title: {
        text: 'Distribuição (%)'
      }
    },
    grid: {
      padding: {
        bottom: 24
      }
    },
    tooltip: {
      shared: false,
      intersect: true,
      custom: ({ seriesIndex, dataPointIndex, w }) => {
        const point = w.config.series[seriesIndex].data[dataPointIndex]
        if (!point || !point.y) return ''
        const [min, q1, median, q3, max] = point.y
        return `
          <div class="apex-tooltip-box" style="padding:8px 12px">
            <strong>${point.x}</strong><br/>
            Min: ${min}%<br/>
            Q1: ${q1}%<br/>
            Média: ${median}%<br/>
            Q3: ${q3}%<br/>
            Max: ${max}%
          </div>
        `
      }
    },
    legend: {
      show: false
    }
  }

  return { options, series }
}

function buildPieChart (selectedKeys, columnList, data) {
  const entries = selectedKeys
    .map(name => {
      const values = columnList.map(column => {
        const stat = data[column][name]
        return stat && stat.mean != null ? Number(stat.mean) : 0
      })
      const avg = values.length
        ? Number((values.reduce((a, b) => a + b, 0) / values.length).toFixed(2))
        : 0
      return {
        label: classesInPortuguese[name],
        value: avg
      }
    })
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'))

  const labels = entries.map(e => e.label)
  const series = entries.map(e => e.value)

  const options = {
    ...getBaseChartConfig(),
    chart: {
      ...getBaseChartConfig().chart,
      type: 'pie'
    },
    labels,
    legend: {
      show: true,
      position: 'bottom'
    },
    tooltip: {
      y: {
        formatter: (val) => `${val} %`
      }
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val.toFixed(1)}%`
    },
    plotOptions: {
      pie: {
        expandOnClick: false
      }
    }
  }

  return { options, series }
}

export function buildStatisticsChart ({
  chartType,
  selectedKeys,
  columnList,
  data,
  groupBy = 'month'
}) {
  if (!selectedKeys.length || !columnList.length || !data) {
    return { options: null, series: [], hasData: false }
  }

  let result
  if (chartType === 'boxPlot') {
    result = buildBoxPlotChart(selectedKeys, columnList, data)
  } else if (chartType === 'pie') {
    result = buildPieChart(selectedKeys, columnList, data)
  } else {
    result = buildBarChart(selectedKeys, columnList, data, groupBy)
  }

  const { options, series } = result
  let hasData = false

  if (chartType === 'pie') {
    hasData = series.some(v => Number(v) > 0)
  } else if (series.length > 0) {
    hasData = true
  }

  return { options, series, hasData }
}
